// apps/web/src/lib/xproduct-nav.ts
//
// KAN-482 F9 — `xproduct:link_followed` from the vendored <cyberfind-nav>.
//
// The nav's shadow root is open and product links are plain anchors. A composed
// click event crosses the shadow boundary, so IntelHub can attach one delegated
// listener to the host element without changing, re-vendoring, or re-SRI-ing the
// shared nav bundle.

import { useEffect } from 'react'
import { capture } from '@intelhub/analytics/client'

type XProductLinkFollowedProperties = {
  target_product: 'cyberfind-io' | 'community'
  surface: 'cross_app_nav'
}

const TARGET_PRODUCTS_BY_HOST: Readonly<Record<string, XProductLinkFollowedProperties['target_product']>> = {
  'cyberfind.io': 'cyberfind-io',
  'www.cyberfind.io': 'cyberfind-io',
  'community.cyberfind.io': 'community',
}

function eventPath(event: MouseEvent): EventTarget[] {
  if (typeof event.composedPath === 'function') return event.composedPath()
  return event.target ? [event.target] : []
}

export function getCrossProductNavClick(
  event: MouseEvent,
  currentOrigin = window.location.origin,
): XProductLinkFollowedProperties | null {
  const anchor = eventPath(event).find(
    (target): target is HTMLAnchorElement => target instanceof HTMLAnchorElement,
  )
  if (!anchor) return null

  let url: URL
  let current: URL
  try {
    url = new URL(anchor.href)
    current = new URL(currentOrigin)
  } catch {
    return null
  }

  if (url.origin === current.origin) return null

  const target_product = TARGET_PRODUCTS_BY_HOST[url.hostname.toLowerCase()]
  if (!target_product) return null

  return { target_product, surface: 'cross_app_nav' }
}

export function useCrossProductNavListener(nav: HTMLElement | null): void {
  useEffect(() => {
    if (!nav) return

    const handleClick = (event: MouseEvent) => {
      const properties = getCrossProductNavClick(event)
      if (!properties) return
      capture('xproduct:link_followed', properties)
    }

    nav.addEventListener('click', handleClick)
    return () => nav.removeEventListener('click', handleClick)
  }, [nav])
}
